import React, {useEffect, useState} from 'react';
import axios from '../Common/axios';
import { useParams } from 'react-router-dom';
import {Tabs, Button, Layout, Spin, Modal} from "antd";
import Plan from './Plan';
import Item from './Item';
import Priority from './Priority';
import ReportHeader from '../Common/ReportHeader';
import {getFindRiskManagement, getFindRiskStatistics, getFindInfoAssetListData, getPdfDownload} from '../Common/api';
import {getCodeListByType} from '../Common/codeManager';
import * as XLSX from 'xlsx';
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import DownloadUrl from '../../components/DownloadUrl';
import {DownloadMd} from '../../components/DownloadPdf';
import MarkdownViewer from '../../components/MarkdownViewer';
import MarkdownViewerBig from '../../components/MarkdownViewerBig';

const {Content } = Layout;
const {TabPane} = Tabs;

// YYYYMMDD / YYYY-MM-DD / 시간 포함 문자열을 모두 YYYY-MM-DD 로 정규화
const formatYmd = (v) => {
    if (v === undefined || v === null || v === '') return '';
    const digits = String(v).replace(/[^0-9]/g, '');
    if (digits.length >= 8) {
        return `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6, 8)}`;
    }
    return String(v);
};

// 등급(H/M/L, 상/중/하, 가/나/다)을 점수(3/2/1)로 치환. 이미 숫자거나 매핑 불가 시 원본 유지
const GRADE_TO_SCORE = { H: 3, M: 2, L: 1, '상': 3, '중': 2, '하': 1, '가': 3, '나': 2, '다': 1 };
const gradeToScore = (v) => {
    if (v === undefined || v === null || v === '') return '';
    const key = String(v).trim().toUpperCase();
    return (key in GRADE_TO_SCORE) ? GRADE_TO_SCORE[key] : v;
};

// report_nm 맨 뒤의 괄호() 안 문자열 추출. 중첩 괄호(예: "(PC(Windows))" → "PC(Windows)") 지원.
// 맨 뒤가 괄호가 아니거나 비어있으면 '' 반환. 전각 괄호（）도 처리.
const extractTrailingParenText = (s) => {
    const str = (s || '').replace(/\s+$/, '');
    if (!/[)）]$/.test(str)) return '';
    let depth = 0;
    const end = str.length - 1;
    let i = end;
    for (; i >= 0; i--) {
        const ch = str[i];
        if (ch === ')' || ch === '）') depth++;
        else if (ch === '(' || ch === '（') { depth--; if (depth === 0) break; }
    }
    return i < 0 ? '' : str.slice(i + 1, end).trim();
};

// 위험관리계획 내보내기 컬럼 정의 (샘플 엑셀 '모바일앱/웹서비스' 시트의 A~Y 순서와 동일)
// 시트에 그대로 붙여넣을 수 있도록 헤더 순서를 맞춤. planList 에 없는 필드는 빈칸 처리.
const EXPORT_COLUMNS = [
    { header: 'Index',           get: (r) => r.asset_cd },
    { header: '진단 code',        get: (r) => r.measure_cd || (r.risk_cd ? String(r.risk_cd).replace(/^R-/, '') : '') },
    { header: '진단항목',          get: (r) => r.measure_nm },
    { header: '진단결과',          get: (r) => r.result_cd },
    { header: '취약점 등급',        get: (r) => gradeToScore(r.measure_grd) },
    { header: '현황',             get: (r) => r.result_desc },
    { header: '위험 시나리오',      get: (r) => r.risks || r.threat_desc || r.scenario || '' },
    { header: '위협 code',        get: (r) => r.threat_cd },
    { header: '위협등급',          get: (r) => gradeToScore(r.threat_grd) },
    { header: '자산명',           get: (r) => r.asset_nm },
    { header: 'IP',              get: (r) => r.asset_ip },
    { header: '중요도',           get: (r) => gradeToScore(r.asset_grd) },
    { header: '위험 code',        get: (r) => r.risk_cd },
    { header: '위험평가',          get: (r) => r.risk_score },
    { header: '보호대책',          get: (r) => r.suggest_desc },
    { header: '대응전략',          get: (r) => r.apply_action },
    { header: '비용',             get: (r) => r.cost_measure },
    { header: '위험도',           get: (r) => r.risk_measure },
    { header: '난이도',           get: (r) => r.difficulty_measure },
    { header: '적용순위',          get: (r) => r.apply_order ?? '' },
    { header: '조치계획일',         get: (r) => formatYmd(r.action_time) },
    { header: '담당자',           get: (r) => r.action_manager },
    { header: '이행평가',          get: (r) => r.implement_eval ?? '' },
    { header: '이행점검 현황',      get: (r) => r.implement_status ?? '' },
    { header: '기타 (미이행사유)',  get: (r) => r.action_remark },
];

function RiskManagement() {
    const params = useParams();
    const loadDefaultMessage = "Loading...";
    const [loadMessage, setLoadMessage] = useState(loadDefaultMessage);

    // 변수 선언
    const reportId = params.reportId;
    const [companyCode, setCompanyCode] = useState('');
    const [companyName, setCompanyName] = useState('');
    const [assetType, setAssetType] = useState('');
    const [reportName, setReportName] = useState('');
    const [tabId, setTabId] = useState('1');
    const [reportInfo, setReportInfo] = useState({});
    const [assetCount, setAssetCount] = useState(0);
    const [isDataChange, setIsDataChange] = useState(false);
    const [spinflag, setSpinflag] = useState(true);
    const [spinflagMain,  setSpinflagMain] = useState(false);
    const [markdownVisible, setMarkdownVisible] = useState(false);
    const [markdownContent, setMarkdownContent] = useState('');

    useEffect(() => {
        if(reportInfo) {
            setCompanyCode(reportInfo.com_cd);
            setAssetType(reportInfo.asset_type);
            setReportName(reportInfo.report_nm);
        }
    }, [reportInfo]);

    // 회사코드(com_cd)로 회사명 조회 (파일명에 사용)
    useEffect(() => {
        if (!companyCode) {
            setCompanyName('');
            return;
        }
        getCodeListByType('com_cd')
            .then((list) => {
                const found = list.find((item) => item.value === companyCode);
                setCompanyName(found ? found.text : '');
            })
            .catch(() => setCompanyName(''));
    }, [companyCode]);

    const [planList, setPlanList] = useState([]);
    const [measureList, setMeasureList] = useState([]);
    const [itemList, setItemList] = useState([]);

    const [sumData, setSumData] = useState({short_term: 0, mid_to_long_term : 0, long_term : 0, 
                        risk_reduction : 0, risk_acception : 0, risk_transfer : 0, risk_evasion : 0, total_risk : 0});

    useEffect(() => {
        findReportList(reportId);
        findAssetCount(reportId);
    }, [reportId]);

    useEffect(() => {
        let s_data = {short_term: 0, mid_to_long_term : 0, long_term : 0, risk_reduction : 0, risk_acception : 0, risk_transfer : 0, risk_evasion : 0, total_risk : 0};
        measureList.map(curr => {
            s_data.short_term = (s_data.short_term + curr.short_term);
            s_data.mid_to_long_term = (s_data.mid_to_long_term + curr.mid_to_long_term);
            s_data.long_term = (s_data.long_term + curr.long_term);
            s_data.risk_reduction= (s_data.risk_reduction + curr.risk_reduction);
            s_data.risk_acception = (s_data.risk_acception + curr.risk_acception);
            s_data.risk_transfer= (s_data.risk_transfer + curr.risk_transfer);
            s_data.risk_evasion = (s_data.risk_evasion + curr.risk_evasion);
            s_data.total_risk = (s_data.total_risk + curr.total_risk);
            return s_data;
        });
        setSumData(s_data);
      }, [measureList]);

    // 현재 리포트의 자산 갯수 조회 (별도 API)
    // tb_va_asset 목록을 조회하여 asset_count(자산 갯수)로 보관한다.
    const findAssetCount = (reportId) => {
        if (!reportId) {
            setAssetCount(0);
            return;
        }
        axios.get(getFindInfoAssetListData(reportId))
            .then((response) => {
                // 응답이 배열이거나 { results: [...] } 형태인 경우 모두 대응
                const data = response.data;
                const list = Array.isArray(data) ? data : (data?.results ?? []);
                setAssetCount(Array.isArray(list) ? list.length : 0);
            })
            .catch((error) => {
                console.log("Riskmanagement.js ------- 자산갯수 조회 실패");
                console.dir(error);
                setAssetCount(0);
            });
    }

    // 레포트 조회
    const findReportList = (reportId) => {
        if(reportId) {
            //통계 조회 (위험관리 riskmgmt + 위험평가 riskeval 동시 조회)
            // 위험관리(riskmgmt)의 statistic_root 에는 위험시나리오(risks)가 없어
            // 위험평가(riskeval)의 statistic_risk_detail 에서 measure_cd 기준으로 risks 를 병합한다.
            Promise.all([
                axios.get(getFindRiskManagement(reportId)),
                axios.get(getFindRiskStatistics(reportId)).catch((error) => {
                    // riskeval 실패 시에도 위험관리 화면은 정상 동작하도록 무시
                    console.log("Riskmanagement.js ------- riskeval 조회 실패");
                    console.dir(error);
                    return null;
                }),
            ])
            .then(([response, riskEvalResponse]) => {
                //console.log(response);
                //setPlanList(response.data.statistic_root);

                // measure_cd -> 위험시나리오(risks) 맵 구성
                const riskByMeasureCd = {};
                const riskDetail = riskEvalResponse?.data?.statistic_risk_detail;
                if (Array.isArray(riskDetail)) {
                    riskDetail.forEach((item) => {
                        if (item && item.measure_cd != null) {
                            riskByMeasureCd[item.measure_cd] = item.risks;
                        }
                    });
                }

                // 데이타 핸들링을 위하여 Key값 추가 + 위험시나리오(risks) 병합
                // measure_cd 가 없으면 risk_cd(R- 접두어 제거)로 폴백 매칭 (EXPORT_COLUMNS 의 '진단 code' 와 동일 규칙)
                const planList = response.data.statistic_root.map((data) => {
                    const measureKey = data.measure_cd || (data.risk_cd ? String(data.risk_cd).replace(/^R-/, '') : '');
                    return {
                        ...data,
                        key: data.id,
                        risks: riskByMeasureCd[measureKey] ?? data.risks ?? '',
                    };
                });

                setPlanList(planList);
                // setItemList(response.data.statistic_risk_cd);
                setItemList(
                    response.data.statistic_risk_cd.sort((a, b) =>
                        a.measure_type.localeCompare(b.measure_type)
                    )
                );
                setMeasureList(response.data.statistic_risk_cd_type);
            })
            .catch((error) => {
                console.log("Riskmanagement.js ------- 82 에러");
                console.dir(error);
                setSpinflag(false);
            })
            .finally(() => {
                setSpinflag(false);
            });
        }
    }

    const items = [
        {
          key: '1',
          label: '위험관리계획',
          children: <Plan companyCode={companyCode} reportId={reportId} planList={planList} setIsDataChange={setIsDataChange} spinflagMain={spinflag} />,
        },
        {
          key: '2',
          label: '위험관리통계',
          children: <Item itemList={itemList} measureList={measureList} sumData={sumData} spinflagMain={spinflag} />,
        },
        {
          key: '3',
          label: '우선순위 분석',
          children: <Priority planList={planList} spinflag={spinflag} />,
        },
    ];

    // 닫기
    const onClickCloseHandler = () => {
        window.close();
    }

    // 탭클릭
    const onChangeTabHandler = (key) => {
        //console.log(key);
        setTabId(key);
    };

    //PDF다운로드
    const pdfDownload = () => {

        const report = document.getElementById('report');
        const data = document.getElementById('divTabs' + tabId);
        const div_height = data.style.height;
        data.style.height = (data.scrollHeight) + 'px';
        html2canvas(report).then((canvas) => {
        const margin = 6; // 출력 페이지 여백설정
        const imgWidth = 198;  // 208
        const pageHeight = 295;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        let heightLeft = imgHeight;
        let position = margin;
        heightLeft -= pageHeight;
        const doc = new jsPDF('p', 'mm', 'a4');
        doc.addImage(canvas, 'PNG', margin, position, imgWidth, imgHeight, '', 'FAST');
        while (heightLeft >= 0) {
            position = heightLeft - imgHeight;
            doc.addPage();
            doc.addImage(canvas, 'PNG', margin, position, imgWidth, imgHeight, '', 'FAST');
            heightLeft -= pageHeight;
        }
        doc.save('RiskManagement_'+reportId+'.pdf');
        });
        //높이 재조정
        data.style.height = div_height;
    };

    // 위험관리계획 데이터를 2차원 배열(헤더+본문)로 변환
    const buildExportAoa = () => {
        const header = EXPORT_COLUMNS.map((c) => c.header);
        const body = (planList || []).map((row) =>
            EXPORT_COLUMNS.map((c) => {
                const v = c.get(row);
                return (v === undefined || v === null) ? '' : v;
            })
        );
        return [header, ...body];
    };

    // 엑셀 / CSV 다운로드
    const onClickDownloadHandler = (format) => {
        // 데이터가 없어도 헤더(필드)만 포함하여 다운로드
        const aoa = buildExportAoa();
        const ws = XLSX.utils.aoa_to_sheet(aoa);

        // 파일명: 위험관리계획_[회사명]_[자산타입]_[자산총갯수]_[날짜]  (자산타입별 시트 자동생성 유틸리티에서 사용)
        const pad = (n) => String(n).padStart(2, '0');
        const now = new Date();
        const dateStr = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}`;
        // 자산타입: report_nm(tb_va_report) 맨 뒤 괄호 () 안 문자열을 사용(중첩 괄호 지원).
        // 괄호가 없거나 괄호 안이 비어있으면 기존 assetType 으로 폴백.
        const typePart = extractTrailingParenText(reportName) || assetType || reportId;
        // 자산 갯수: 별도 API로 조회한 asset_count 사용. (조회 실패 시 0 → planList 길이로 폴백)
        const totalCount = assetCount || planList.length;
        // 회사명은 com_cd 로 조회. 비어있으면 companyCode 로 폴백.
        const companyPart = companyName || companyCode || '';
        // 회사명/자산타입은 [..] 로 감쌈: 이후 _ 로 분리할 때 '_'(예: PC_Window)가 있어도 구분 가능
        const baseName = `위험관리계획_[${companyPart}]_[${typePart}]_${totalCount}_${dateStr}`;

        if (format === 'csv') {
            // 한글 깨짐 방지를 위해 UTF-8 BOM 추가
            const csv = '﻿' + XLSX.utils.sheet_to_csv(ws);
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${baseName}.csv`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } else {
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, '위험관리계획');
            XLSX.writeFile(wb, `${baseName}.xlsx`);
        }
    };

    const updateMarkdownInChunks = async (data) => {
        const chunkSize = 100000; // 100KB씩 처리
        let processedData = '';
        
        console.log("청크 단위로 데이터 처리 시작...");
        
        for (let i = 0; i < data.length; i += chunkSize) {
            const chunk = data.slice(i, i + chunkSize);
            processedData += chunk;
            
            // 중간 상태 업데이트
            setMarkdownContent(processedData);
            
            console.log(`처리됨: ${Math.round((i + chunk.length) / data.length * 100)}%`);
            
            // 브라우저가 UI를 업데이트할 시간 제공
            await new Promise(resolve => setTimeout(resolve, 10));
        }
        
        console.log("청크 처리 완료");
        setMarkdownVisible(true);
        setSpinflagMain(false);
    };    

    const titleName="보고서 생성";
    const pdfDownLoadReport = (event) =>{
        setSpinflagMain(true);
        setLoadMessage("잠시만 기다려 주세요. 보고서 작성중 입니다...");
        let titleName="보고서 생성";

        const buttonType = event.currentTarget.textContent;

        if (buttonType === 'Markdown') {
            axios.get(getPdfDownload("riskmgmt_md", reportId)).then((response) => {
                
                let nowDataSize=response.headers['content-length'];
                
                setMarkdownContent(response.data);
                setMarkdownVisible(true);
                setSpinflagMain(false);           
                // console.log("응답 수신, 청크 처리 시작");
                // updateMarkdownInChunks(response.data);                
                // console.log("then 블록 진입 - 시작");
                // console.log("응답 상태1:", response);
                // console.log("응답 상태2:", response.headers['content-length']);
                // console.log("응답 존재:", !!response);
                // console.log("데이터 존재:", !!response.data);
                // console.log("데이터 타입:", typeof response.data);
                // console.log("출력");
                // try {
                //     console.log("데이터 처리 시작");
                //     setMarkdownContent(response.data);
                //     console.log("setMarkdownContent 완료");
                    
                //     setMarkdownVisible(true);
                //     console.log("setMarkdownVisible 완료");
                    
                //     setSpinflagMain(false);
                //     console.log("setSpinflagMain 완료");
                    
                // } catch (error) {
                //     console.error("데이터 처리 중 에러:", error);
                // }

            }).catch((error) => {
                Modal.error({title:titleName,content:"Markdown 생성 중에 에러가 발생하였습니다.", okText:"Close"});
                setSpinflagMain(false);
            });  
        }else{
            axios.get(getPdfDownload("riskmgmt", reportId)).then((response) => {
                if(response.data.status_url){
                    DownloadUrl(
                        response.data.status_url,
                        () => setSpinflagMain(false), // onSuccess  
                        (errorMessage) => {
                            Modal.error({title:titleName,content:errorMessage, okText:"Close"});
                            setSpinflagMain(false);
                        }
                    );
                }else{
                    Modal.error({title:titleName,content:"다운로드 중에 에러가 발생하였습니다.", okText:"Close"});
                    setSpinflagMain(false);
                }
            }).catch((error) => {
                Modal.error({title:titleName,content:"다운로드 중에 에러가 발생하였습니다.", okText:"Close"});
                setSpinflagMain(false);
            });    
        }    
    }

    // 정보 변경시 재조회
    useEffect(() => {
        if(isDataChange) {
            findReportList(reportId);
        }
    }, [isDataChange]);
/** -------------------------------------------------------------------------- **/
    return (
        <Spin size={"large"}  tip={"Loading..."} spinning={spinflagMain}>
        <Layout id="report" style={{overflow: 'hidden'}}>
            <div className='popup_header'>
                <div className='popup_title'>
                    위험관리 : 계획 및 통계
                </div>
            </div>
            <div className="popup_button_report" style={{top: '10px', width: '560px'}}>
                <Button
                        type="default"
                        onClick={() => onClickDownloadHandler('csv')}
                        style={{marginLeft: '10px'}}
                        data-html2canvas-ignore="true"
                    >
                        {'CSV'}
                </Button>
                <Button
                        type="default"
                        onClick={pdfDownLoadReport}
                        style={{marginLeft: '10px'}}
                        data-html2canvas-ignore="true"
                    >
                        {'Markdown'}
                </Button>
                <Button
                        type="default"
                        onClick={pdfDownLoadReport}
                        style={{marginLeft: '10px'}}
                        data-html2canvas-ignore="true"
                    >
                        {'보고서'}
                </Button>
                <Button
                    type="default"
                    onClick={onClickCloseHandler}
                    style={{marginLeft: '10px'}}
                    data-html2canvas-ignore="true"
                >
                    {'닫기'}
                </Button>
            </div>
            <div className="section" id="reportHeader">
                <ReportHeader reportId={reportId} reportInfo={reportInfo} setReportInfo={setReportInfo}/>
            </div>
            <div className="section" style={{borderTop: '0'}}>
                 <Content style={{ backgroundColor: '#f5f5f5'}}>
                    {/* <Tabs defaultActiveKey="1" type={'card'} onChange={onChangeTabHandler}>
                        <TabPane tab={"위험관리계획"} key="1">
                            <Plan companyCode={companyCode} reportId={reportId} planList={planList} setIsDataChange={setIsDataChange} spinflag={spinflag} />
                        </TabPane>
                        <TabPane tab={"위험관리통계"} key="2">
                            <Item itemList={itemList} measureList={measureList} sumData={sumData} spinflag={spinflag} />
                        </TabPane>
                    </Tabs> */}
                    <Tabs defaultActiveKey="1" type={'card'} onChange={onChangeTabHandler} items={items}></Tabs>
                </Content>
            </div>
        </Layout>
        <MarkdownViewerBig
            visible={markdownVisible}
            onClose={() => setMarkdownVisible(false)}
            markdownContent={markdownContent}
            reportId={reportId}
            reportName={reportName}
        />
        </Spin>
    );
}

export default RiskManagement;